import { useState, useEffect, useCallback } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { isGeoApplicable, useGeoValue } from '@entities/geo';
import { useUserRole } from '@entities/user';
import { getTypedObjectEntries } from '@shared/types/typedObjectEntries';

export const updateParams = <T extends object>(
  filters: URLSearchParams,
  updates: T,
  page?: string,
  size?: string,
) => {
  const newParams = new URLSearchParams(filters);

  page && newParams.set('page', page);
  size && newParams.set('size', size);

  getTypedObjectEntries(updates).forEach(([key, value]) => {
    const stringKey = String(key);
    const stringValue = String(value);

    const isMultipleKeys = stringKey.match(/^(.+)\[\d+\]$/);

    if (!value) {
      newParams.delete(stringKey);
      return;
    }

    // handle multiple keys
    if (isMultipleKeys) {
      const clearKey = stringKey.replace(/\[\d+\]$/, '');

      if (newParams.has(clearKey) && newParams.getAll(clearKey).includes(stringValue)) {
        newParams.delete(clearKey);
      }

      newParams.append(clearKey, stringValue);

      return;
    }

    newParams.set(stringKey, stringValue);
  });

  return newParams;
};

interface IProps {
  hasPagination?: boolean;
  disableUrlFilter?: boolean;
}

export const useFilter = <T extends object>({
  hasPagination = true,
  disableUrlFilter = false,
}: IProps = {}) => {
  const isWalletsMonitoringPage =
    window.location.pathname.startsWith('/wallets') ||
    window.location.pathname.startsWith('/wallets-legacy');
  const [initialSize] = useState(isWalletsMonitoringPage ? '10' : '50');
  const [initialPage] = useState('1');

  const navigate = useNavigate();
  const geoValue = useGeoValue();
  const userRole = useUserRole();
  const hasGeo = isGeoApplicable(userRole);
  const [searchParams] = useSearchParams({
    page: initialPage,
    size: initialSize,
  });

  const [page, setPage] = useState(disableUrlFilter ? initialPage : searchParams.get('page'));
  const [size, setSize] = useState(disableUrlFilter ? initialSize : searchParams.get('size'));

  const initialFilter = hasPagination ? { page, size } : {};
  const [currentFilter, setCurrentFilter] = useState(
    new URLSearchParams(!disableUrlFilter ? searchParams.toString() : initialFilter),
  );

  useEffect(() => {
    if (hasPagination) {
      const currentPage = currentFilter.get('page') || initialPage;
      const currentSize = currentFilter.get('size') || initialSize;
      if (page === currentPage && size === currentSize) return;
      setCurrentFilter(prev => updateParams(prev, {} as T, page, size));
    }
  }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  // update URL
  useEffect(() => {
    if (disableUrlFilter) return;

    const params = new URLSearchParams(currentFilter.toString());
    if (hasGeo) params.set('geo', geoValue);
    navigate({ search: params.toString() });
  }, [currentFilter, disableUrlFilter, geoValue, hasGeo, navigate]);

  const updateFilter = useCallback(
    (updates: T) => {
      setCurrentFilter(prev => updateParams<T>(prev, updates));
    },
    [page, size], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const resetFilter = useCallback(() => {
    hasPagination && setPage(initialPage);
    hasPagination && setSize(initialSize);

    const newParams = new URLSearchParams(initialFilter);
    const geo = searchParams.get('geo');
    if (hasGeo && geo) newParams.set('geo', geo);
    setCurrentFilter(newParams);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteFilterKey = useCallback((key: string) => {
    setCurrentFilter(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(key);
      return newParams;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    currentFilter,
    updateFilter,
    resetFilter,
    deleteFilterKey,

    page,
    setPage,
    size,
    setSize,
  };
};
